import { ref } from 'vue';
import { useTheme } from 'vuetify';

// Single source of truth for the dark-mode preference, shared across every
// component that calls useDarkMode() (module-level ref). Toggling it from the
// app bar instantly reflects everywhere that reads `isDarkMode`.
const STORAGE_KEY = 'darkMode';
// Exported for the handful of readers that only observe the preference and have
// no business changing it (or calling Vuetify's useTheme(), which useDarkMode()
// does and which is only legal inside setup()).
export const isDarkMode = ref(localStorage.getItem(STORAGE_KEY) === 'true');

// The Vuetify theme controller, captured on first use. Vuetify's useTheme() may
// only be called from setup(), but the preference can also be changed from
// outside a component (the demo frame switches it over postMessage), so the
// controller has to be reachable from module scope once a component has bound it.
let themeController = null;

/**
 * The colour the OS paints an installed PWA's title bar, per theme.
 *
 * These are Vuetify `surface` in each theme (main.js): the light one falls
 * through to Vuetify's default white, the dark one is set explicitly. `surface`
 * rather than `primary` because appBar.vue passes no `color` prop, so the bar
 * the title bar has to blend into is the surface colour.
 *
 * Kept in step from here rather than from a `prefers-color-scheme` media query
 * on the tag itself: dark mode in this app follows the persisted preference
 * below, not the OS, and the two disagree often enough that an OS-keyed title
 * bar would sit lit above a dark app.
 */
const THEME_COLOR = { light: '#ffffff', dark: '#1e1e1e' };

function syncThemeColor() {
  const meta = document.querySelector('meta[name="theme-color"]');
  // Absent by design in the demo build, which ships no manifest and no tag.
  if (!meta) return;
  meta.setAttribute('content', isDarkMode.value ? THEME_COLOR.dark : THEME_COLOR.light);
}

/**
 * Dark-mode controller.
 *
 * Keeps three things in lock-step:
 *   1. the persisted preference (localStorage)
 *   2. the `.dark` class on <html> (drives the CSS-variable design tokens)
 *   3. the active Vuetify theme (drives every Vuetify component's surfaces/text)
 *
 * Must be called from within a component `setup()` (it uses Vuetify's
 * useTheme()).
 */
export function useDarkMode() {
  const theme = useTheme();
  themeController = theme;

  const apply = () => {
    document.documentElement.classList.toggle('dark', isDarkMode.value);
    theme.change(isDarkMode.value ? 'customThemeDark' : 'customTheme');
    syncThemeColor();
  };

  // Ensure the Vuetify theme matches the persisted preference on first use
  // (the <html> class is already set synchronously in main.js to avoid a flash).
  apply();

  const toggleDarkMode = () => {
    isDarkMode.value = !isDarkMode.value;
    localStorage.setItem(STORAGE_KEY, String(isDarkMode.value));
    apply();
  };

  return { isDarkMode, toggleDarkMode };
}

/**
 * Set the preference from outside a component.
 *
 * Same three things in lock-step as above, minus the toggle semantics. Used by
 * the demo build, where the surrounding page owns the light/dark switch; the
 * Vuetify half is skipped (harmlessly) if no component has called useDarkMode()
 * yet, because the persisted value is read at boot anyway.
 *
 * @param {boolean} value
 */
export function setDarkMode(value) {
  isDarkMode.value = Boolean(value);
  localStorage.setItem(STORAGE_KEY, String(isDarkMode.value));
  document.documentElement.classList.toggle('dark', isDarkMode.value);
  syncThemeColor();
  if (themeController) {
    themeController.change(isDarkMode.value ? 'customThemeDark' : 'customTheme');
  }
}
